import React, { useState } from 'react';
import AppHeader from '../../../shared/components/AppHeader';
import { calendarEvents } from '../lib/calendarEvents';
import '../styles/calendario.css';

const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const monthName = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });
const fullDate = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const dateLabel = (date) => new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
const expandedEvents = Object.fromEntries(Object.entries(calendarEvents).map(([group, events]) => {
  const byDate = {};
  events.forEach((event) => {
    const date = new Date(`${event.start}T12:00:00Z`);
    const end = new Date(`${event.end}T12:00:00Z`);
    while (date <= end) {
      const key = date.toISOString().slice(0, 10);
      (byDate[key] ||= []).push(event);
      date.setUTCDate(date.getUTCDate() + 1);
    }
  });
  return [group, byDate];
}));

export default function CalendarioPage() {
  const today = new Date();
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(() => iso(today));
  const [group, setGroup] = useState('1-8');
  const eventsByDate = expandedEvents[group];
  const entries = (date) => eventsByDate[date] || [];
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = Array.from({ length: Math.ceil((firstWeekday + daysInMonth) / 7) * 7 }, (_, index) => {
    const day = index - firstWeekday + 1;
    return day > 0 && day <= daysInMonth ? day : null;
  });
  const selectedInMonth = selected.startsWith(`${year}-${String(monthIndex + 1).padStart(2, '0')}-`);
  const focused = selectedInMonth && entries(selected).length > 0;
  const visibleEvents = focused
    ? entries(selected).map((event) => ({ date: selected, event }))
    : calendarEvents[group].filter((event) => event.start <= iso(new Date(year, monthIndex + 1, 0)) && event.end >= iso(month))
      .map((event) => ({ date: event.start < iso(month) ? iso(month) : event.start, event }))
      .sort((a, b) => a.date.localeCompare(b.date));

  function changeMonth(offset) {
    const next = new Date(year, monthIndex + offset, 1);
    setMonth(next);
    setSelected('');
  }

  return <div className="calendar-screen">
    <AppHeader title="Calendário" />
    <main className="calendar-page">
      <div className="calendar-layout">
        <section className="calendar-panel" aria-label="Calendário mensal">
          <div className="calendar-toolbar">
            <h3>{monthName.format(month)}</h3>
            <div className="calendar-controls">
              <button type="button" onClick={() => { setMonth(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(iso(today)); }}>Hoje</button>
              <button type="button" className="calendar-period-toggle" onClick={() => setGroup(group === '1-8' ? '9-12' : '1-8')} aria-label={`Períodos ${group === '1-8' ? '1º ao 8º' : '9º ao 12º'}. Toque para alternar`}>{group === '1-8' ? '1º ao 8º' : '9º ao 12º'}</button>
              <button type="button" onClick={() => changeMonth(-1)} aria-label="Mês anterior">‹</button>
              <button type="button" onClick={() => changeMonth(1)} aria-label="Próximo mês">›</button>
            </div>
          </div>
          <div className="calendar-grid" style={{ '--calendar-weeks': cells.length / 7 }}>
            {weekdays.map((day) => <div className="calendar-weekday" key={day}>{day}</div>)}
            {cells.map((day, index) => {
              if (!day) return <div className="calendar-empty" key={`empty-${index}`} />;
              const date = iso(new Date(year, monthIndex, day));
              const dayEvents = entries(date);
              return <button key={date} type="button" className={`calendar-day${date === selected ? ' is-selected' : ''}${date === iso(today) ? ' is-today' : ''}`} onClick={() => setSelected(date)} aria-label={`${fullDate.format(new Date(year, monthIndex, day))}${dayEvents.length ? `, ${dayEvents.length} evento${dayEvents.length > 1 ? 's' : ''}` : ''}`} aria-pressed={date === selected}>
                <span className="calendar-day-number">{day}</span>
                {dayEvents.length > 0 && <span className="calendar-day-events">{dayEvents.slice(0, 2).map((event, eventIndex) => <span key={`${event.title}-${eventIndex}`} className={`calendar-event-pill kind-${event.kind || 'academic'}`}>{event.title}</span>)}{dayEvents.length > 2 && <span className="calendar-more">+{dayEvents.length - 2}</span>}</span>}
              </button>;
            })}
          </div>
        </section>
        <aside className="calendar-agenda" aria-label="Eventos acadêmicos">
          <div className="calendar-agenda-heading">
            <p className="calendar-eyebrow">Agenda</p>
            <h3>{focused ? fullDate.format(new Date(`${selected}T12:00:00`)) : `Eventos de ${monthName.format(month)}`}</h3>
            {focused && <button type="button" onClick={() => setSelected('')} className="calendar-show-all">Ver todos do mês</button>}
          </div>
          {visibleEvents.length ? <div className="calendar-agenda-list">{visibleEvents.map(({ date, event }, index) => <article className="calendar-agenda-item" key={`${date}-${index}`}>
            <div className="calendar-agenda-date"><strong>{Number(date.slice(8))}</strong><span>{new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(`${date}T12:00:00`))}</span></div>
            <div><span className={`calendar-agenda-kind kind-${event.kind || 'academic'}`}>{event.kind === 'holiday' ? 'Feriado / recesso' : event.kind === 'assessment' ? 'Avaliação' : 'Acadêmico'}</span><h4>{event.title}</h4>{event.start !== event.end && <p>{dateLabel(event.start)} a {dateLabel(event.end)}</p>}</div>
          </article>)}</div> : <p className="calendar-no-events">Nenhum evento cadastrado neste mês.</p>}
        </aside>
      </div>
    </main>
  </div>;
}
